import { useEffect } from "react";
import {
  SITE_NAME,
  LOCALE,
  TWITTER,
  DEFAULT_TITLE,
  DEFAULT_DESCRIPTION,
  DEFAULT_KEYWORDS,
  DEFAULT_OG_IMAGE,
  absUrl,
} from "./siteConfig";
import { organizationSchema, websiteSchema } from "./schema";
import { getActiveLang } from "../lib/activeLang";
import { localizePath, isNonLocalized } from "../lib/locale";

const OG_LOCALE = { it: "it_IT", en: "en_US", de: "de_DE", fr: "fr_FR" };
const HREFLANG = { it: "it-IT", en: "en", de: "de-DE", fr: "fr-FR" };

// Rimuove SOLO il placeholder iniettato da emergent-main.js (nodo NON gestito
// da React). Non tocca mai i tag creati da React (title/meta/link/og): rimuoverli
// causa il crash "deletedFiber.parentNode.removeChild" durante la navigazione.
function stripForeignMeta() {
  document.head.querySelectorAll('meta[name="description"]').forEach((m) => {
    if (m.getAttribute("content") === "A product of emergent.sh") m.remove();
  });
}

// React 19 hoists <title>/<meta>/<link> into <head> automatically.
export default function Seo({
  title,
  description = DEFAULT_DESCRIPTION,
  path = "/",
  image = DEFAULT_OG_IMAGE,
  type = "website",
  keywords = DEFAULT_KEYWORDS,
  noindex = false,
  statusCode,
  jsonLd = [],
  article,
}) {
  const fullTitle = title ? `${title} · TORRI` : DEFAULT_TITLE;
  const lang = getActiveLang();
  const localizable = !isNonLocalized(path);
  const canonical = absUrl(localizable ? localizePath(path, lang) : path);
  const ogImage = image ? absUrl(image) : DEFAULT_OG_IMAGE;
  const schemas = [organizationSchema(), websiteSchema(), ...jsonLd].filter(Boolean);
  const alternates = localizable
    ? [
        ...["it", "en", "de", "fr"].map((l) => ({ hreflang: HREFLANG[l], href: absUrl(localizePath(path, l)) })),
        { hreflang: "x-default", href: absUrl(localizePath(path, "it")) },
      ]
    : [];

  useEffect(() => {
    stripForeignMeta();
    const obs = new MutationObserver(stripForeignMeta);
    obs.observe(document.head, { childList: true });
    const t = setTimeout(() => obs.disconnect(), 6000);
    return () => {
      obs.disconnect();
      clearTimeout(t);
    };
  }, [description]);

  return (
    <>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {keywords?.length ? <meta name="keywords" content={keywords.join(", ")} /> : null}
      <link rel="canonical" href={canonical} />
      {alternates.map((a) => (
        <link key={a.hreflang} rel="alternate" hrefLang={a.hreflang} href={a.href} />
      ))}
      {statusCode ? <meta name="prerender-status-code" content={String(statusCode)} /> : null}
      <meta
        name="robots"
        content={noindex ? "noindex, nofollow" : "index, follow, max-image-preview:large"}
      />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={OG_LOCALE[lang] || LOCALE} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      {article?.publishedTime ? (
        <meta property="article:published_time" content={article.publishedTime} />
      ) : null}
      {article?.section ? <meta property="article:section" content={article.section} /> : null}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      {TWITTER ? <meta name="twitter:site" content={TWITTER} /> : null}

      {schemas.map((s, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(s) }}
        />
      ))}
    </>
  );
}
